import React, { FC, ReactNode, useRef } from 'react'

import { defineMessages, useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'

import { hideTooltipsAction } from 'actions/tooltipAction'
import { SharingMethod } from 'api/sharingContentApi'
import { DefaultLoader } from 'components/presentational/AnimatedWatch/AnimatedWatch'
import { ButtonLink } from 'components/presentational/link'
import { CopyUrlSvg } from 'components/presentational/svg/CopyUrlSvg'
import { FacebookSvg } from 'components/presentational/svg/FacebookSvg'
import { ViberSvg } from 'components/presentational/svg/ViberSvg'
import { WhatsUpSvg } from 'components/presentational/svg/WhatsUpSvg'
import { TooltipDefaultContent } from 'components/presentational/Tooltip'
import { SharingMethodSocialMap } from 'reducers/sharing/sharing.function'

import { sharingMethodClipBoard } from './SharingContent.constant'
import {
  StyledSharingContentWrapper,
  StyledSharingItemWrapper,
  StyledSharingSvgWrapper,
  StyledSharingTooltipDefaultContentItem,
  TextAreaVisuallyHidden,
} from './SharingContent.styled'
import { SharingContentProps, WrapperProps } from './SharingContent.types'
import { copyToClipboard } from '../../../../client/functions/copyToClipboard'

const messages: Record<string, Record<string, string>> = defineMessages({
  url: { id: 'sharing.text.url', defaultMessage: 'Скопировать ссылку' },
  cancel: { id: 'form.button.cancel', defaultMessage: 'Отмена' },
})

export const mapSvgList: Record<string, ReactNode> = {
  url: <CopyUrlSvg />,
  whatsapp: <WhatsUpSvg />,
  facebook: <FacebookSvg size={40} />,
  viber: <ViberSvg />,
}

const Wrapper: FC<WrapperProps> = ({ children, alignmentStyleCss }) => (
  <StyledSharingContentWrapper alignmentStyleCss={alignmentStyleCss}>
    <TooltipDefaultContent>{children}</TooltipDefaultContent>
  </StyledSharingContentWrapper>
)

export const SharingContentView: FC<SharingContentProps & WrapperProps> = ({
  sharingMethods,
  alignmentStyleCss,
  fetchUniNoticeAction,
  shareHitMethodStatisticsAction,
}) => {
  const dispatch = useDispatch()
  const intl = useIntl()
  /**
   * TODO: нужно создать специальную скрытую ноду и поместить туда ссылку шаринга и далее с помощью объекта range и document.execCommand
   */
  const sharingNodeRef = useRef<HTMLTextAreaElement>(null)
  const handleCancel = () => dispatch(hideTooltipsAction())

  const handleShare = (sharingObject: SharingMethod) => {
    if (sharingObject.sharingMethod === sharingMethodClipBoard) {
      if (fetchUniNoticeAction) {
        fetchUniNoticeAction()
      }

      copyToClipboard(sharingObject.url)
    }

    if (shareHitMethodStatisticsAction) {
      shareHitMethodStatisticsAction(sharingObject.sharingMethod)
    }

    handleCancel()
  }

  if (sharingMethods.length === 0) {
    return (
      <Wrapper alignmentStyleCss={alignmentStyleCss}>
        <DefaultLoader />
      </Wrapper>
    )
  }

  return (
    <Wrapper alignmentStyleCss={alignmentStyleCss}>
      {sharingMethods.map((sharingObject) => {
        if (sharingObject.sharingMethod === sharingMethodClipBoard) {
          return (
            <StyledSharingItemWrapper
              data-name={`sharing-method-${sharingObject.sharingMethod}-action`}
              key={sharingObject.sharingMethod}
              onClick={() => handleShare(sharingObject)}
            >
              <StyledSharingSvgWrapper>
                {mapSvgList[sharingObject.sharingMethod]}
              </StyledSharingSvgWrapper>
              <StyledSharingTooltipDefaultContentItem>
                <ButtonLink key={sharingObject.sharingMethod}>
                  {intl.formatMessage(messages[sharingObject.sharingMethod])}
                </ButtonLink>
              </StyledSharingTooltipDefaultContentItem>
              <TextAreaVisuallyHidden
                ref={sharingNodeRef}
                value={sharingObject.url}
                readOnly={true}
                onChange={() => null}
              />
            </StyledSharingItemWrapper>
          )
        }
        return (
          <StyledSharingItemWrapper
            key={sharingObject.sharingMethod}
            data-name={`sharing-method-${sharingObject.sharingMethod}-action`}
            href={sharingObject.url}
            target="_blank"
            onClick={() => handleShare(sharingObject)}
          >
            <StyledSharingSvgWrapper>
              {mapSvgList[sharingObject.sharingMethod]}
            </StyledSharingSvgWrapper>
            <StyledSharingTooltipDefaultContentItem>
              <ButtonLink key={sharingObject.sharingMethod}>
                {SharingMethodSocialMap[sharingObject.sharingMethod]?.title}
              </ButtonLink>
            </StyledSharingTooltipDefaultContentItem>
          </StyledSharingItemWrapper>
        )
      })}

      <StyledSharingItemWrapper
        key="sharing-method-cancel"
        data-name="sharing-method-cancel-action"
        onClick={() => handleCancel()}
      >
        <StyledSharingTooltipDefaultContentItem>
          <ButtonLink>{intl.formatMessage(messages.cancel)}</ButtonLink>
        </StyledSharingTooltipDefaultContentItem>
      </StyledSharingItemWrapper>
    </Wrapper>
  )
}
